import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { memo, type ReactNode } from 'react';

import { themeStyles } from '../theme/theme';

type ISize = 'md' | 'sm' | 'xs';

export interface BarProps {
  value: number;
  color?: string;
  size?: ISize;
  label?: ReactNode;
  title?: ReactNode;
  endTitle?: ReactNode;
}

const sizes: Record<ISize, string> = {
  md: '24px',
  sm: '16px',
  xs: '8px',
};

const Component = memo(({ value, color, label, title, endTitle, size }: BarProps) => {
  return (
    <Root>
      {title && <Title>{title}</Title>}
      <Line $size={size}>
        <Progress $percents={value} $color={color}>
          <Label $percents={value}>{label}</Label>
        </Progress>
      </Line>
      {endTitle && <Title>{endTitle}</Title>}
    </Root>
  );
});

const Root = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Title = styled.div`
  ${themeStyles.text.telka.body.md};
  ${themeStyles.text.ellipsis};
  flex: 1;
  flex-shrink: 0;
`;

const Line = styled.div<{ $size?: ISize }>`
  flex: 3;
  display: flex;
  align-items: center;
  background-color: #e4e8f5;
  border-radius: 24px;
  height: ${({ $size }) => ($size ? sizes[$size] : '24px')};
`;

const Progress = styled.div<{ $percents: number; $color?: string }>`
  width: ${(p) => p.$percents.toFixed(2)}%;
  height: 100%;
  background-color: ${(p) => p.$color || '#3250FA'};
  border-radius: 99px;
  transition: 1s width;
  position: relative;
`;

const Label = styled.div<{ $percents: number }>`
  position: absolute;
  top: 50%;

  ${(p) =>
    p.$percents > 50
      ? css`
          right: 12px;
          color: white;
          transform: translateY(-50%);
        `
      : css`
          color: black;
          right: -12px;
          transform: translateY(-50%) translateX(100%);
        `};
`;

export const Bar = Object.assign(Component, {
  Root,
  Line,
  Progress,
  Label,
  Title,
});
